import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Bucket from "@/services/bucket/bucket";

export interface BucketCountsParams {
  organizationId: string;
  companyId?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useBucketCounts(
  params: Ref<BucketCountsParams | null> | BucketCountsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["bucketCounts", "disabled"];
    return ["bucketCounts", p.organizationId, p.companyId || "all"];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const result = await Bucket.getBucketCounts({
        organizationId: p.organizationId,
        companyId: p.companyId,
      });

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load bucket counts");
    },
    enabled,
    staleTime: 30 * 1000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000,
  });
}
